/* eslint-disable no-loop-func */
import { checkIsLoop, checkIsLoopEnd } from "@/core/util";

interface RemovalNode {
  data: { origId: string; type?: string; uiData?: any };
}

interface RemovalEdge {
  source: string;
  target: string;
}

export interface RemovalGraphLookup {
  findNode: (id: string) => RemovalNode | undefined;
  findIncoming: (id: string) => RemovalEdge | undefined;
  findOutgoing: (id: string) => RemovalEdge | undefined;
}

/**
 * Build once in a computed owned by the diagram. Read the deletion-relevant
 * fields reactively here so the walks can use plain snapshots without losing
 * invalidation on in-place edits. Keep the first match, exactly like find().
 * Hidden edges intentionally participate in these deletion rules.
 */
export function createRemovalGraphLookup(
  nodes: any[],
  edges: any[]
): RemovalGraphLookup {
  const byOrigId = new Map<string, RemovalNode>();
  const incomingLeft = new Map<string, RemovalEdge>();
  const outgoingRight = new Map<string, RemovalEdge>();

  // Guarded because this builder is eager where find() short-circuited: a
  // malformed entry past the first match used to go untouched, and a throw here
  // empties a computed that four slot templates render from, blanking the canvas.
  nodes.forEach((node) => {
    const { origId, type, uiData } = node?.data ?? {};
    if (!byOrigId.has(origId)) {
      byOrigId.set(origId, { data: { origId, type, uiData } });
    }
  });
  edges.forEach((edge) => {
    const { source, target, sourceHandle, targetHandle } = edge ?? {};
    const snapshot = { source, target };
    if (targetHandle?.includes("left") && !incomingLeft.has(target)) {
      incomingLeft.set(target, snapshot);
    }
    if (sourceHandle?.includes("right") && !outgoingRight.has(source)) {
      outgoingRight.set(source, snapshot);
    }
  });

  return {
    findNode: (id) => byOrigId.get(id),
    findIncoming: (id) => incomingLeft.get(id),
    findOutgoing: (id) => outgoingRight.get(id),
  };
}

// Preserve the original search behavior for callers without a shared lookup.
function createRemovalGraphSearch(
  nodes: any[],
  edges: any[]
): RemovalGraphLookup {
  return {
    findNode: (id) => nodes.find((node) => node.data.origId === id),
    findIncoming: (id) =>
      edges.find(
        (edge) => edge.target === id && edge.targetHandle?.includes("left")
      ),
    findOutgoing: (id) =>
      edges.find(
        (edge) => edge.source === id && edge.sourceHandle?.includes("right")
      ),
  };
}

/**
 *  Find if a node is inside a loop and count steps in that loop
 */
export function getLoopInfo(
  nodeId: string,
  nodes: any[],
  edges: any[],
  lookup?: RemovalGraphLookup
): { isInLoop: boolean; stepCount: number } {
  const { findNode, findIncoming, findOutgoing } =
    lookup || createRemovalGraphSearch(nodes, edges);
  let loopStartId: string | null = null;
  let currentId = nodeId;

  // Traverse backwards to find LOOP start
  let incoming = findIncoming(currentId);

  while (incoming) {
    const prevNode = findNode(incoming.source);
    if (!prevNode) {
      break;
    }

    if (prevNode.data?.type === "LOOP" || checkIsLoop(prevNode.data)) {
      loopStartId = prevNode.data.origId;
      break;
    }

    if (prevNode.data?.type === "LOOP_END" || checkIsLoopEnd(prevNode.data)) {
      return { isInLoop: false, stepCount: 0 };
    }

    currentId = incoming.source;
    incoming = findIncoming(currentId);
  }

  if (!loopStartId) {
    return { isInLoop: false, stepCount: 0 };
  }

  // Count steps from LOOP start to LOOP_END
  let stepCount = 0;
  currentId = loopStartId;

  let outgoing = findOutgoing(currentId);

  while (outgoing) {
    const nextNode = findNode(outgoing.target);
    if (!nextNode) {
      break;
    }

    if (nextNode.data?.type === "LOOP_END" || checkIsLoopEnd(nextNode.data)) {
      break;
    }

    stepCount++;
    currentId = outgoing.target;
    outgoing = findOutgoing(currentId);
  }

  return { isInLoop: true, stepCount };
}

export function canRemoveNode(
  nodeId: string,
  nodes: any[],
  edges: any[],
  lookup?: RemovalGraphLookup
): boolean {
  if (!nodes || !edges) {
    return true;
  }

  const graph = lookup || createRemovalGraphSearch(nodes, edges);
  const { findNode, findIncoming, findOutgoing } = graph;
  const currentNode = findNode(nodeId);

  // Case 1: Don't allow LOOP_END nodes to be deleted
  if (currentNode?.data?.type === "LOOP_END") {
    return false;
  }

  const incomingEdge = findIncoming(nodeId);
  const outgoingEdge = findOutgoing(nodeId);

  if (!incomingEdge || !outgoingEdge) {
    return true;
  }

  // Find the actual connected nodes
  const prevNode = findNode(incomingEdge.source);
  const nextNode = findNode(outgoingEdge.target);

  const nextIsRouter = nextNode?.data?.type === "ROUTER";
  const nextIsLoop =
    nextNode?.data?.type === "LOOP" || checkIsLoop(nextNode?.data);

  // Case 2: Step right before a LOOP/ROUTER start - allow deletion if there are multiple steps before it.
  if (nextIsLoop || nextIsRouter) {
    let stepCount = 1;
    let currentId = nodeId;
    let reachedStart = false;

    // Traverse backwards and count steps until we hit start or a structural node.
    let incoming = findIncoming(currentId);

    while (incoming) {
      const prevStepNode = findNode(incoming.source);
      if (!prevStepNode) {
        break;
      }

      const prevType = prevStepNode.data?.type;
      // Stop counting if we hit start or a structural node (LOOP, LOOP_END, ROUTER).
      if (
        incoming.source === "start" ||
        prevStepNode.data?.origId === "start"
      ) {
        reachedStart = true;
        break;
      }

      if (
        prevType === "LOOP" ||
        prevType === "LOOP_END" ||
        prevType === "ROUTER" ||
        checkIsLoop(prevStepNode.data)
      ) {
        break;
      }

      stepCount++;
      currentId = incoming.source;
      incoming = findIncoming(currentId);
    }

    // LOOP keeps the legacy mandatory-body behavior. ROUTER only applies this
    // guard for the start -> step -> router placeholder case.
    if ((nextIsLoop || reachedStart) && stepCount <= 1) {
      return false;
    }
  }

  // Case 3: Step inside a LOOP - check if there are multiple steps in the loop
  // at least one mandatory step case
  const loopInfo = getLoopInfo(nodeId, nodes, edges, graph);
  if (loopInfo.isInLoop && loopInfo.stepCount <= 1) {
    return false;
  }

  // Case 4: Step between two ROUTER nodes
  if (prevNode?.data?.type === "ROUTER" && nextNode?.data?.type === "ROUTER") {
    return false;
  }

  // All other cases
  return true;
}

export function isNodeDetachedFromFlow(nodeData: any): boolean {
  if (!nodeData) return false;
  if (hasSkipState(nodeData)) return false;

  const hasBrokenIncomingChain =
    nodeData.missingIncomingChain === true &&
    nodeData.skipMissingIncomingChain !== true;
  const hasConfirmedIncomingChain = nodeData.missingIncomingChain === false;

  return (
    hasBrokenIncomingChain ||
    (nodeData.active === false && !hasConfirmedIncomingChain)
  );
}

function hasSkipState(nodeData: any): boolean {
  if (!nodeData?.uiData) return false;

  try {
    const uiData =
      typeof nodeData.uiData === "string"
        ? JSON.parse(nodeData.uiData)
        : nodeData.uiData;
    const skipState = uiData?.skipState;

    return Boolean(
      skipState?.directlySkipped === true ||
        skipState?.inheritedSkipped === true
    );
  } catch {
    return false;
  }
}

/**
 * Find a step by its origId in a nested step structure
 */
export function findStepByOrigId(
  steps: any[],
  id: string
): { steps: any[]; index: number; step: any } | null {
  let result: { steps: any[]; index: number; step: any } | null = null;

  steps.forEach((step, index) => {
    if (result) return;

    if (!step.type || step.type === "LOOP" || step.type === "LOOP_END") {
      if (step.origId === id) {
        result = { steps, index, step };
      }
    } else {
      // ROUTER type
      if (step.origId === id) {
        result = { steps, index, step };
      }
      if (step.routes) {
        step.routes.forEach((route: any) => {
          if (result) return;
          const res = findStepByOrigId(route.steps, id);
          if (res) result = res;
        });
      }
    }
  });

  return result;
}

/**
 * Check if the next or previous step is a LOOP
 *
 * Superseded in the diagram by createLoopNeighborLookup. Retained because
 * nodeRenderLookups.spec.ts runs it as the parity oracle for that lookup —
 * delete it and the equivalence test loses what it compares against.
 */
export function isNextStepLoop(stepList: any[], id: string): boolean {
  const currentStep = findStepByOrigId(stepList, id);

  if (currentStep) {
    const { steps, index } = currentStep;
    if (checkIsLoop(steps[index + 1]) || checkIsLoop(steps[index - 1])) {
      return true;
    }
  }

  return false;
}

/**
 * Check if the next step (via right handle edge) is a LOOP_END
 *
 * Superseded in the diagram by createNextLoopEndLookup, and retained as that
 * lookup's parity oracle in nodeRenderLookups.spec.ts.
 */
export function isNextStepLoopEnd(
  edges: any[],
  nodes: any[],
  stepOrigId: string
): boolean {
  // Find the edge going OUT from this step (right handle)
  const outgoingEdge = edges.find(
    (edge) =>
      edge.source === stepOrigId &&
      edge.sourceHandle?.includes("right") &&
      !edge.hidden
  );

  if (!outgoingEdge) return false;

  // Find the target node
  const targetNode = nodes.find(
    (node) => node.data?.origId === outgoingEdge.target
  );

  return targetNode ? checkIsLoopEnd(targetNode.data) : false;
}

/**
 *  Collect ALL origIds including nested router steps
 */
export const collectAllOrigIds = (stepsList: any[]): Set<string> => {
  const ids = new Set<string>();
  stepsList.forEach((s) => {
    if (s.origId) ids.add(s.origId);
    if (s.type === "ROUTER" && s.routes) {
      s.routes.forEach((route: any) => {
        if (route.steps) {
          collectAllOrigIds(route.steps).forEach((i) => ids.add(i));
        }
      });
    }
  });
  return ids;
};
